import axios, {
    CancelToken,
} from 'axios'
import {
    createPlaceBetRequest,
    createBuildBetRequest,
} from './apiTransformers'
import {
    isUnauthorized,
} from './apiErrors'
import { isLocalStorageAvailable } from '../utils/localStorage';
import { getCookie } from '../utils/cookie';

const API_VERSIONS = {
    ONE: '1.0',
    THREE: '3.0',
}

const TEMPLATES = {
    BET_HISTORY: 'bet-history/%bookie%',
    EXTERNAL_BET_HISTORY: 'bet-history/%bookie%',
    EXTERNAL_ACCOUNT: 'deposit/%bookie%',
    CUSTOMER_STATUS: 'customer-status',
    USER_INFO: 'customer/%bookie%',
    LOGIN: 'log-in/%bookie%',
    LOGOUT: 'log-in/%bookie%',
    EXTERNAL_LOGOUT: 'log-out/%bookie%',
    AUTH_INFO: 'log-in/%bookie%',
    APP_CONFIG: 'api/stubs/bookmaker-offers-tier-one/',
    TOP_UP: 'deposit/%bookie%',
    CARD_DETAILS: 'deposit/%bookie%',
    PLACE_BET: 'bets/%bookie%',
    BUILD_BETS: 'bet-selections/%bookie%',
    BUILD_MULTIPLES: 'bet-selections/%bookie%', // Should be swapped out for speculation endpoint
}

const getRequestConfig = () => {
    const config = {
        headers: {
            'Content-Type': 'application/vnd.api+json',
            'Client-Id': 'web',
        },
    };

    switch (isLocalStorageAvailable()) {
        case true:
            if (window.localStorage.getItem('auth-token')) {
                config.headers['auth-token'] = window.localStorage.getItem('auth-token');
            }
            break;
        case false:
            if (getCookie('auth-token')) {
                config.headers['auth-token'] = getCookie('auth-token');
            }
            break;

        // no default
    }

    return config;
}

/**
 * Configure API hosts based on document attributes and location
 *
 * NOTE: Contains fallbacks:
 * - V1 will fallback to ''
 * - V3 will fallback to `https://betslip-api.racingpost.com/v3`
 *
 * Normally both API attributes are required, but in tests they are often undefined.
 *
 * @return {Object} { v1: 'example.com/api', v3: 'example.com/api/v3/' }
 */
const getApiUrls = () => {
    const API_V1_ATTR = document.querySelector('[data-betslip-api-url]')
    const API_V1_FALLBACK = ''
    const apiV1Url = API_V1_ATTR ?
        API_V1_ATTR.getAttribute('data-betslip-api-url') :
        API_V1_FALLBACK
    let apiV3Url;
    const bookmakerStubsData = `https://${window.location.hostname}`;

    if (window.location.hostname.indexOf('dev.racingpost') !== -1) {
        apiV3Url = 'https://uat-betslip-api.racingpost.com/v3';
    } else if (window.location.hostname.indexOf('test1.racingpost') !== -1) {
        apiV3Url = 'https://uat-betslip-api.racingpost.com/v3';
    } else if (window.location.hostname.indexOf('uat.racingpost') !== -1) {
        apiV3Url = 'https://uat-betslip-api.racingpost.com/v3';
    } else {
        apiV3Url = 'https://betslip-api.racingpost.com/v3';
    }

    return {
        v1: apiV1Url,
        v3: apiV3Url,
        bookmakersData: bookmakerStubsData,
    }
}

const generateBookieEndpoint = (apiVersion, template, bookie) => {
    const API_URLS = getApiUrls()
    let host;
    switch (apiVersion) {
        case API_VERSIONS.ONE:
            host = API_URLS.v1;
            break;
        case API_VERSIONS.THREE:
            host = API_URLS.v3;
            break;
        default:
            host = API_URLS.v3;
    }
    const path = template.replace('%bookie%', bookie)

    return `${host}/${path}`
}

const autoLogout = (error, dispatch, onUnauthorized) => {
    if (isUnauthorized(error)) {
        dispatch(onUnauthorized)
    }

    return Promise.reject(error)
}

// Requests

const loadAppConfig = () => {
    const API_URLS = getApiUrls()
    const endpoint = `${API_URLS.bookmakersData}/${TEMPLATES.APP_CONFIG}`

    return axios.get(endpoint)
}

const login = (bookie, body) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.LOGIN, bookie)

    return axios.post(endpoint, body, getRequestConfig())
}

const logout = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.LOGOUT, bookie)

    return axios.delete(endpoint, getRequestConfig())
}

const externalLogout = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.EXTERNAL_LOGOUT, bookie)

    return axios.get(endpoint, getRequestConfig())
}

const loadBookmakerLoginInfo = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.AUTH_INFO, bookie)

    return axios.get(endpoint, getRequestConfig())
}

const getExternalAccountUrl = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.EXTERNAL_ACCOUNT, bookie)

    return axios.get(endpoint, getRequestConfig())
}

const getBetHistory = (bookie, requestParams) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.BET_HISTORY, bookie)
    const config = getRequestConfig();

    return axios.get(endpoint, {
        ...config,
        params: requestParams,
    })
}

const getExternalBetHistoryUrl = (bookie) => {
    const endpoint = generateBookieEndpoint(
        API_VERSIONS.THREE,
        TEMPLATES.EXTERNAL_BET_HISTORY,
        bookie,
    )

    return axios.get(endpoint, getRequestConfig())
}

const getUserInfo = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.USER_INFO, bookie)

    return axios.get(endpoint, getRequestConfig())
}

const getCustomerStatus = () => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.CUSTOMER_STATUS)

    return axios.get(endpoint, getRequestConfig())
}

const getCardDetails = (bookie) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.CARD_DETAILS, bookie)

    return axios.get(endpoint, getRequestConfig())
}

const topUp = (bookie, body) => {
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.TOP_UP, bookie)

    return axios.post(endpoint, body, getRequestConfig())
}

let buildBetsSource = CancelToken.source()

const buildBets = (bookie, selectedBets, dispatch, onUnauthorized) => {
    const body = createBuildBetRequest(selectedBets)
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.BUILD_BETS, bookie)
    const config = getRequestConfig();

    buildBetsSource.cancel();
    buildBetsSource = CancelToken.source();

    return axios.post(endpoint, body, {
        ...config,
        cancelToken: buildBetsSource.token,
    })
        .then(response => Promise.resolve(response))
        .catch((error) => {
            if (axios.isCancel(error) === false) {
                return autoLogout(error, dispatch, onUnauthorized)
            }
            return Promise.reject(error)
        })
}

let buildMultiplesSource = CancelToken.source()

const buildMultiples = (bookie, selectedBets, dispatch, onUnauthorized) => {
    const body = createBuildBetRequest(selectedBets)
    const endpoint = generateBookieEndpoint(
        API_VERSIONS.THREE,
        TEMPLATES.BUILD_MULTIPLES,
        bookie,
    )
    const config = getRequestConfig();

    buildMultiplesSource.cancel();
    buildMultiplesSource = CancelToken.source();

    return axios.post(endpoint, body, {
        ...config,
        cancelToken: buildMultiplesSource.token,
    })
        .then(response => Promise.resolve(response))
        .catch((error) => {
            if (axios.isCancel(error) === false) {
                return autoLogout(error, dispatch, onUnauthorized)
            }
            return Promise.reject(error)
        })
}

const cancelBuildMultiples = () => {
    buildMultiplesSource.cancel()
}

const placeBet = (
    bookie,
    selectedBets,
    stakedMultiples,
    selectedFreeBetId,
    dispatch,
    onUnauthorized,
) => {
    const body = createPlaceBetRequest(selectedBets, stakedMultiples, selectedFreeBetId)
    const endpoint = generateBookieEndpoint(API_VERSIONS.THREE, TEMPLATES.PLACE_BET, bookie)

    return axios.post(endpoint, body, getRequestConfig())
        .then(response => Promise.resolve(response))
        .catch(error => autoLogout(error, dispatch, onUnauthorized))
}

const ApiClient = {
    // API V1
    loadAppConfig,
    login,
    logout,
    externalLogout,
    loadBookmakerLoginInfo,
    getExternalAccountUrl,
    getBetHistory,
    getExternalBetHistoryUrl,
    getCustomerStatus,
    getUserInfo,
    getCardDetails,
    topUp,

    // API Betting
    buildBets,
    buildMultiples,
    cancelBuildMultiples,
    placeBet,

    // Other
    getApiUrls,
}

export default ApiClient



// WEBPACK FOOTER //
// /src/js/common/utils/apiClient.js